// PlatformShell.tsx — console shell + router.
//
// SCOPED TO `PT`. This tree imports platformTheme, never theme.ts's `T`. The
// operator console must be visually distinguishable from the tenant product at a
// glance — see platformTheme.ts's header for why that is a safety property.
//
// TWELVE nav items in three groups, matching the design's Layout shell:
//   Monitor   — Overview · Usage & Spend · Reliability · Security
//   Customers — Customers · Billing · Adoption · Setup Wizard
//   Platform  — Provisioning Defaults · Operators · Settings · Audit
//
// Overview is the FLEET HEALTH dashboard (FleetOverview), bound to
// /health/platform. The spend/latency screen that previously occupied "Overview"
// is Usage & Spend, where the design puts it; Latency is a section inside it,
// not a top-level item.
//
// Icons are the prototype's own Lucide-style SVG paths, inlined — the app has no
// icon dependency and the console should not add one for twelve glyphs.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PT } from "../../platformTheme";
import { pget } from "./api";
import { UsageOverview } from "./UsageOverview";
import { FleetOverview } from "./FleetOverview";
import { Reliability } from "./Reliability";
import { Security } from "./Security";
import { NotBuilt, BILLING } from "./NotBuilt";
import { Adoption } from "./Adoption";
import { CoreMetrics } from "./CoreMetrics";
import { CustomersList, CustomerDetail, type Tenant } from "./Customers";
import { SetupWizard } from "./SetupWizard";
import { ProvisioningDefaults } from "./ProvisioningDefaults";
import { Operators } from "./Operators";
import { AuditScreen } from "./AuditScreen";
import { Settings } from "./Settings";
import { CommandPalette, type PaletteItem } from "./CommandPalette";

export type ConsoleScreen =
  | "overview" | "usage" | "reliability" | "security"
  | "customers" | "billing" | "adoption" | "wizard"
  | "defaults" | "operators" | "settings" | "audit";

interface NavItem { id: ConsoleScreen; label: string; icon: string; hint: string }

// Paths lifted from the prototype's navItem() calls.
const GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: "Monitor",
    items: [
      { id: "overview", label: "Overview", icon: "M3 3h8v8H3zM13 3h8v5h-8zM13 12h8v9h-8zM3 15h8v6H3z", hint: "Fleet health at a glance" },
      { id: "usage", label: "Usage & Spend", icon: "M3 3v18h18M7 14l3-4 3 3 5-7", hint: "AI cost, tokens and latency" },
      { id: "reliability", label: "Reliability", icon: "M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6zM9 12l2 2 4-4", hint: "Provider errors and percentiles" },
      { id: "security", label: "Security", icon: "M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4", hint: "2FA, SSO and sensitive actions" },
    ],
  },
  {
    label: "Customers",
    items: [
      { id: "customers", label: "Customers", icon: "M3 21h18M6 21V7l6-4 6 4v14M10 9h.01M14 9h.01M10 13h.01M14 13h.01", hint: "Lifecycle, users, integrations" },
      { id: "billing", label: "Billing", icon: "M2 7h20v10H2zM2 11h20", hint: "Requires backend not yet built" },
      { id: "adoption", label: "Adoption", icon: "M22 12h-4l-3 9L9 3l-3 9H2", hint: "Sign-ins, feature use, per-customer trend" },
      { id: "wizard", label: "Setup Wizard", icon: "M5 3h14v18l-7-4-7 4zM9 8h6M9 12h4", hint: "The sole customer-creation path" },
    ],
  },
  {
    label: "Platform",
    items: [
      { id: "defaults", label: "Provisioning Defaults", icon: "M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 15h6", hint: "What new customers are seeded with" },
      { id: "operators", label: "Operators", icon: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87", hint: "Who holds cross-tenant access" },
      { id: "settings", label: "Settings", icon: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6M19.4 13a7.5 7.5 0 0 0 0-2l1.8-1.3-1.8-3.1-2.1.8a7.5 7.5 0 0 0-1.7-1L15.2 4H8.8l-.4 2.3a7.5 7.5 0 0 0-1.7 1l-2.1-.8-1.8 3.1L4.6 11a7.5 7.5 0 0 0 0 2l-1.8 1.3 1.8 3.1 2.1-.8a7.5 7.5 0 0 0 1.7 1l.4 2.3h6.4l.4-2.3a7.5 7.5 0 0 0 1.7-1l2.1.8 1.8-3.1z", hint: "Operator SSO and global AI" },
      { id: "audit", label: "Audit", icon: "M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01", hint: "Cross-tenant operator actions" },
    ],
  },
];

const ALL: NavItem[] = GROUPS.flatMap((g) => g.items);
const TITLE = Object.fromEntries(ALL.map((i) => [i.id, i.label])) as Record<ConsoleScreen, string>;
const HINT = Object.fromEntries(ALL.map((i) => [i.id, i.hint])) as Record<ConsoleScreen, string>;

const Icon: React.FC<{ d: string; color: string }> = ({ d, color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none" }} aria-hidden>
    <path d={d} />
  </svg>
);

export const PlatformShell: React.FC<{ initial?: ConsoleScreen; user?: any; onSignOut?: () => void }> = ({
  initial = "overview", user, onSignOut,
}) => {
  const [screen, setScreen] = useState<ConsoleScreen>(initial);
  const [days, setDays] = useState(30);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // `uptime` is a STRING here on purpose: serverStats.uptime is an object
  // ({seconds, formatted}) and rendering it raw threw React #31, which took the
  // whole console down behind the error boundary. Only `.formatted` is stored.
  const [engine, setEngine] = useState<{ ok: boolean; uptime?: string } | null>(null);

  const loadTenants = useCallback(async () => {
    const r = await pget("/tenants");
    if (r.ok) setTenants(r.data?.tenants || []);
  }, []);
  useEffect(() => { loadTenants(); }, [loadTenants, reloadKey]);

  // Engine chip in the sidebar footer — real, not decorative. It reflects whether
  // the platform-health probe answers, so a red dot means something.
  useEffect(() => {
    let alive = true;
    const probe = async () => {
      const r = await pget("/health/platform");
      if (!alive) return;
      const up = r.ok ? r.data?.serverStats?.uptime?.formatted : undefined;
      setEngine({ ok: r.ok, uptime: typeof up === "string" ? up : undefined });
    };
    probe();
    const id = setInterval(probe, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setPaletteOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = useCallback((s: ConsoleScreen) => { setTenant(null); setScreen(s); }, []);
  const openCustomer = useCallback((t: Tenant) => { setScreen("customers"); setTenant(t); }, []);

  const paletteItems: PaletteItem[] = useMemo(() => [
    ...ALL.map((s) => ({ id: `screen:${s.id}`, label: s.label, sub: s.hint, kind: "screen" as const, run: () => go(s.id) })),
    ...tenants.map((t) => ({
      id: `tenant:${t.tenant_id}`, label: t.name, sub: `${t.edition} · ${t.status}`,
      kind: "customer" as const, run: () => openCustomer(t),
    })),
  ], [tenants, go, openCustomer]);

  const showWindow = screen === "usage" || screen === "reliability" || screen === "adoption";
  const operatorName = user?.name || user?.email || "Operator";
  const initials = String(operatorName).trim().slice(0, 2).toUpperCase();

  return (
    <div style={{ fontFamily: PT.fontUI, background: PT.bg, color: PT.tx, minHeight: "100%", display: "flex" }}>
      {/* ── Sidebar ── */}
      <aside style={{
        width: 238, flexShrink: 0, background: PT.bg2, borderRight: `1px solid ${PT.brd}`,
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ padding: "14px 16px 12px", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${PT.chBlue}, ${PT.chViolet})`, flex: "none" }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>KontractAI</div>
            <div style={{ fontSize: 9.5, color: PT.txD, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 600 }}>
              Platform Console
            </div>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", paddingBottom: 8 }}>
          {GROUPS.map((g) => (
            <div key={g.label} style={{ marginBottom: 10 }}>
              <div style={{
                fontSize: 9.5, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase",
                color: "#9AA3B2", padding: "8px 16px 5px",
              }}>
                {g.label}
              </div>
              {g.items.map((s) => {
                const on = s.id === screen;
                return (
                  <button
                    key={s.id}
                    onClick={() => go(s.id)}
                    title={s.hint}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                      fontFamily: PT.fontUI, fontSize: 12.5, fontWeight: on ? 600 : 500,
                      padding: "7px 16px", border: "none", cursor: "pointer",
                      background: on ? PT.accS : "transparent",
                      color: on ? PT.acc : PT.txM,
                      boxShadow: on ? `inset 2px 0 0 ${PT.acc}` : "none",
                    }}
                  >
                    <Icon d={s.icon} color={on ? PT.acc : PT.txD} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Engine status chip */}
        <div style={{ padding: "10px 16px", borderTop: `1px solid ${PT.brdSubtle}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11, color: PT.txM }}>
            <span style={{
              width: 7, height: 7, borderRadius: 4, flex: "none",
              background: engine === null ? PT.txD : engine.ok ? PT.ok : PT.err,
            }} />
            {engine === null ? "Checking engine…" : engine.ok ? "Engine nominal" : "Engine unreachable"}
          </div>
          {engine?.uptime && (
            <div style={{ fontSize: 10, color: PT.txD, fontFamily: PT.fontMono, marginTop: 3 }}>
              uptime {engine.uptime}
            </div>
          )}
        </div>

        {/* Operator identity + sign out */}
        <div style={{ padding: "11px 13px", borderTop: `1px solid ${PT.brdSubtle}`, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8, background: PT.accS, color: PT.acc,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 600, fontSize: 11.5, flex: "none",
          }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {operatorName}
            </div>
            <div style={{ fontSize: 10, color: PT.txD }}>Operator</div>
          </div>
          {onSignOut && (
            <button
              onClick={onSignOut}
              title="Sign out"
              style={{ background: "transparent", border: "none", cursor: "pointer", color: PT.txD, padding: 4, lineHeight: 0 }}
            >
              <Icon d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" color={PT.txD} />
            </button>
          )}
        </div>
      </aside>

      {/* ── Main ── */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <header style={{
          display: "flex", alignItems: "center", gap: 12, padding: "11px 20px", height: 58, boxSizing: "border-box",
          background: PT.bg2, borderBottom: `1px solid ${PT.brd}`,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: -0.2 }}>{TITLE[screen]}</div>
            <div style={{ fontSize: 11, color: PT.txD }}>{HINT[screen]}</div>
          </div>
          <span style={{
            fontFamily: PT.fontMono, fontSize: 10, fontWeight: 600, letterSpacing: 0.6,
            padding: "3px 7px", borderRadius: PT.radius,
            color: PT.acc, background: PT.accS, border: `1px solid ${PT.accS2}`,
          }}>
            OPERATOR
          </span>

          <div style={{ flex: 1 }} />

          <button
            onClick={() => setPaletteOpen(true)}
            style={{
              fontFamily: PT.fontUI, fontSize: 12, color: PT.txD, background: PT.bgInset,
              border: `1px solid ${PT.brd}`, borderRadius: 8, padding: "6px 12px",
              cursor: "pointer", width: 236, textAlign: "left", display: "flex", alignItems: "center", gap: 8,
            }}
          >
            <Icon d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3" color={PT.txD} />
            <span style={{ flex: 1 }}>Search…</span>
            <span style={{ fontFamily: PT.fontMono }}>⌘K</span>
          </button>

          {showWindow && (
            <label style={{ fontSize: 12, color: PT.txM, display: "flex", alignItems: "center", gap: 6 }}>
              Window
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                style={{
                  fontFamily: PT.fontUI, fontSize: 12, padding: "4px 8px", borderRadius: PT.radius,
                  border: `1px solid ${PT.brd}`, background: PT.bg2, color: PT.tx,
                }}
              >
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
            </label>
          )}
        </header>

        <main style={{ flex: 1, padding: 24, overflowY: "auto" }}>
          <div style={{ maxWidth: 1340, margin: "0 auto" }}>
            {screen === "overview" && (
              <FleetOverview
                onOpenCustomer={openCustomer}
                onGoUsage={() => go("usage")}
                onGoCustomers={() => go("customers")}
              />
            )}
            {screen === "usage" && <UsageOverview days={days} />}
            {screen === "reliability" && <><Reliability days={days} /><CoreMetrics days={days} /></>}
            {screen === "security" && <Security onOpenCustomer={openCustomer} />}

            {screen === "billing" && <NotBuilt spec={BILLING} />}
            {screen === "adoption" && <Adoption days={days} />}

            {screen === "customers" && (
              tenant
                ? <CustomerDetail
                    tenant={tenant}
                    onBack={() => { setTenant(null); setReloadKey((k) => k + 1); }}
                    onChanged={() => setReloadKey((k) => k + 1)}
                  />
                : <CustomersList key={reloadKey} onOpen={setTenant} />
            )}

            {screen === "wizard" && (
              <SetupWizard
                onProvisioned={(id) => {
                  setReloadKey((k) => k + 1);
                  setScreen("customers");
                  pget("/tenants").then((r) => {
                    if (r.ok) {
                      const list: Tenant[] = r.data?.tenants || [];
                      setTenants(list);
                      const t = list.find((x) => x.tenant_id === id);
                      if (t) setTenant(t);
                    }
                  });
                }}
              />
            )}

            {screen === "defaults" && <ProvisioningDefaults />}
            {screen === "operators" && <Operators />}
            {screen === "audit" && <AuditScreen tenants={tenants} />}
            {screen === "settings" && <Settings />}
          </div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={paletteItems} />
    </div>
  );
};
